
import SEO from "../components/seo.jsx";
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Star } from "lucide-react";
import useGoogleReviews from "../hooks/useGoogleReviews.js";

gsap.registerPlugin(ScrollTrigger);

const cinzel = { fontFamily: "'Cinzel', serif" };

function QuoteMark() {
  return (
    <svg
      width="26"
      height="20"
      viewBox="0 0 26 20"
      fill="none"
      className="text-[#c9a84c]/25"
    >
      <path
        d="M0 20V11.7C0 4.9 4.4 0.6 10.8 0L11.6 3.2C7.9 4 5.9 6.2 5.6 9.5H10.4V20H0ZM14.4 20V11.7C14.4 4.9 18.8 0.6 25.2 0L26 3.2C22.3 4 20.3 6.2 20 9.5H24.8V20H14.4Z"
        fill="currentColor"
      />
    </svg>
  );
}

function StarRating({ rating = 0, size = 15 }) {
  return (
    <div
      className="flex items-center gap-0.5"
      aria-label={`${rating} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          fill={star <= rating ? "#D6A63D" : "none"}
          color={star <= rating ? "#D6A63D" : "#C9BFAE"}
        />
      ))}
    </div>
  );
}

function GoogleReviewCard({ review }) {
  const name = review.authorName || "Google user";
  const initial = name.charAt(0).toUpperCase();

  return (
    <article className="relative mx-3 flex w-[300px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border border-[#c9a84c]/25 bg-white p-6 sm:w-[340px] sm:p-7">
      <span className="absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/60 to-transparent" />

      <div className="flex items-center justify-between gap-3">
        <StarRating rating={review.rating} />
        <a
          href={review.reviewUri || review.reviewsUri || "https://www.google.com/maps"}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View review on Google"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-[#4285F4] text-xs font-bold text-white"
        >
          G
        </a>
      </div>

      <QuoteMark />

      <p className="mt-4 mb-6 line-clamp-6 min-h-[145px] text-[13px] leading-[1.85] text-[#6b5535]">
        {review.text}
      </p>

      <div className="mt-auto flex items-center gap-3 border-t border-[#c9a84c]/15 pt-4">
        {review.authorPhoto ? (
          <img
            src={review.authorPhoto}
            alt=""
            referrerPolicy="no-referrer"
            className="h-11 w-11 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div
            style={cinzel}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] text-xs font-black text-[#1a0f02]"
          >
            {initial}
          </div>
        )}

        <div className="min-w-0 flex-1">
          {review.authorUri ? (
            <a
              href={review.authorUri}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate text-[12px] font-bold uppercase tracking-[0.04em] text-[#1a0f02] hover:underline"
              style={cinzel}
            >
              {name}
            </a>
          ) : (
            <p
              style={cinzel}
              className="truncate text-[12px] font-bold uppercase tracking-[0.04em] text-[#1a0f02]"
            >
              {name}
            </p>
          )}

          <p className="mt-1 text-[10.5px] uppercase tracking-[0.08em] text-[#a07828]">
            {review.relativeTime || "Google Review"}
          </p>
        </div>
      </div>
    </article>
  );
}


function MarqueeRow({ items, direction = "left", speed = 42 }) {
  const trackRef = useRef(null);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
  });
  const [isDragging, setIsDragging] = useState(false);

  const doubledItems = [...items, ...items];

  function handlePointerDown(e) {
    // Only enable mouse dragging with the primary button.
    if (e.pointerType === "mouse" && e.button !== 0) return;

    const track = trackRef.current;
    if (!track) return;

    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScroll: track.scrollLeft,
    };

    setIsDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!dragRef.current.active) return;

    const track = trackRef.current;
    if (!track) return;

    const distance = e.clientX - dragRef.current.startX;
    track.scrollLeft = dragRef.current.startScroll - distance;
  }

  function stopDragging() {
    dragRef.current.active = false;
    setIsDragging(false);
  }

  return (
    <div
      ref={trackRef}
      className={`marquee-row-mask ${isDragging ? "is-dragging" : ""}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
      onDragStart={(e) => e.preventDefault()}
    >
      <div
        className="marquee-track"
        style={{
          animationDuration: `${speed}s`,
          animationDirection: direction === "left" ? "normal" : "reverse",
        }}
      >
        {doubledItems.map((review, index) => (
          <GoogleReviewCard
            key={`${review.id || review.authorName}-${index}`}
            review={review}
          />
        ))}
      </div>
    </div>
  );
}


function ReviewsSkeleton() {
  return (
    <div className="flex gap-6 overflow-hidden px-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="h-[280px] w-[300px] flex-shrink-0 animate-pulse rounded-2xl bg-white/70 sm:w-[340px]"
        />
      ))}
    </div>
  );
}

export default function ClientTestimonials({
  url = "https://www.hairclinique.in/client-testimonials.php",
}) {
  const pageRef = useRef(null);
  const headRef = useRef(null);
  const subRef = useRef(null);

  const { status, data, error } = useGoogleReviews();

  const reviews = (data?.reviews || []).filter(
    (review) => review.text?.trim()
  );

  const rowA = reviews.filter((_, index) => index % 2 === 0);
  const rowB = reviews.filter((_, index) => index % 2 !== 0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";

      gsap.fromTo(
        headRef.current,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 0.75, ease }
      );

      gsap.fromTo(
        subRef.current,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.12 }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="Google Reviews | Hair Transplant Reviews | Dr. Bhavesh Gupta"
        description="Read Google reviews and patient experiences for Dr. Bhavesh Gupta at Clinique Internationale, Kharghar."
        url={url}
      />

      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .marquee-row-mask {
          position: relative;
          overflow: hidden;
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }

        .marquee-track {
          display: flex;
          align-items: stretch;
          width: max-content;
          animation-name: marquee-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }

        .marquee-row-mask:hover .marquee-track {
          animation-play-state: paused;
        }

        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
          .marquee-row-mask {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: pan-y;
}

.marquee-row-mask.is-dragging {
  cursor: grabbing;
}

.marquee-row-mask.is-dragging .marquee-track {
  animation-play-state: paused;
}

.marquee-row-mask img {
  -webkit-user-drag: none;
}
      `}</style>

      <section
        ref={pageRef}
        className="relative mt-20 w-full overflow-hidden bg-[#f2ece0] py-16 sm:py-20 md:py-24"
        style={{ fontFamily: "'Raleway', sans-serif" }}
      >
        <div className="mx-auto mb-12 max-w-6xl px-5 text-center sm:mb-14 sm:px-8 lg:px-12">
          <p
            style={cinzel}
            className="mb-3 flex items-center justify-center gap-2.5 text-[10.5px] uppercase tracking-[0.28em] text-[#a07828]"
          >
            <span className="h-px w-6 bg-[#c9a84c]" />
            Google Reviews
            <span className="h-px w-6 bg-[#c9a84c]" />
          </p>

          <h2
            ref={headRef}
            style={cinzel}
            className="mb-4 text-[1.5rem] font-black uppercase tracking-[0.04em] text-[#1a0f02] sm:text-[1.9rem] md:text-[2.2rem]"
          >
            What Our Clients Say
          </h2>

          <p
            ref={subRef}
            className="mx-auto max-w-xl text-[14px] leading-relaxed text-[#6b5535] sm:text-[15px]"
          >
            Real Google reviews from patients who trusted us with their hair
            restoration journey.
          </p>
        </div>

        <div className="mx-auto mb-10 flex max-w-6xl flex-wrap items-center justify-center gap-5 px-5 sm:gap-8">
          <div className="text-center">
            <p style={cinzel} className="text-xs uppercase tracking-widest text-[#a07828]">
              {data?.businessName || "HairClinique"}
            </p>
          </div>

          {typeof data?.rating === "number" && (
            <div className="flex items-center gap-3">
              <span style={cinzel} className="text-3xl font-bold text-[#1a0f02]">
                {data.rating.toFixed(1)}
              </span>
              <div>
                <StarRating rating={Math.round(data.rating)} size={17} />
                <p className="mt-1 text-xs text-[#6b5535]">Google rating</p>
              </div>
            </div>
          )}

          {typeof data?.userRatingCount === "number" && (
            <p className="text-sm text-[#6b5535]">
              {data.userRatingCount.toLocaleString()} Google reviews
            </p>
          )}

          {data?.reviewsUri && (
            <a
              href={data.reviewsUri}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-[#c9a84c]/50 px-5 py-2.5 text-xs font-semibold text-[#6b5535] transition hover:bg-white"
            >
              View all reviews on Google ↗
            </a>
          )}
        </div>

        {status === "loading" && <ReviewsSkeleton />}

        {status === "error" && (
          <p className="px-5 text-center text-sm text-[#6b5535]">
            Google reviews are temporarily unavailable. Please try again later.
            {import.meta.env.DEV && error ? ` (${error})` : ""}
          </p>
        )}

        {status === "ready" && reviews.length === 0 && (
          <p className="px-5 text-center text-sm text-[#6b5535]">
            No written Google reviews are currently available.
          </p>
        )}

        {status === "ready" && reviews.length > 0 && (
          <div className="flex flex-col gap-6">
            <MarqueeRow
              items={rowA.length ? rowA : reviews}
              direction="left"
              speed={46}
            />
            <MarqueeRow
              items={rowB.length ? rowB : reviews}
              direction="right"
              speed={52}
            />
          </div>
        )}
      </section>
    </>
  );
}
